import { ValidatorFn, Validators } from '@angular/forms';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import orderBy from 'lodash/orderBy';

import { arrayItemValidator, numberInteger, typeNumber } from '../shared/form.utils';
import { Gear, GearConfigParameter, GearConfigArray, ParameterValue } from '../shared/models/gear.model';

// Does the gear have only 1 required file input?
export function hasSingleInput(gear: Gear): boolean {
  const requiredInputs = Object.values(gear.gear.inputs)
    .filter(input => input.base === 'file' && !input.optional);
  return requiredInputs.length === 1;
}

// Does the gear require a read-write API key?
export function requiresReadWriteKey(gear: Gear): boolean {
  return Object.values(gear.gear.inputs).some(input => input.base === 'api-key' && !input['read-only']);
}

// Can we run with a default configuration and 0-1 inputs?
export function supportsAlwaysLatest(gear: Gear): boolean {
  const requiredFileInputs = Object.values(gear.gear.inputs)
    .filter(input => input.base === 'file' && !input.optional);
  const requiredConfigParams = Object.values(gear.gear.config)
    .filter(config => !(('default' in config) || config.type === 'boolean' || config.optional));
  return requiredFileInputs.length <= 1 && requiredConfigParams.length === 0;
}

export function orderByLatest(gears: Gear[]): Gear[] {
  return orderBy(gears, 'created', 'desc');
}

/**
 * Convert JSON Schema properties to Angular form control validators.
 */
export function getConfigValidators(config: GearConfigParameter): ValidatorFn[] {
  const validators = [];
  const hasDefault = ('default' in config);
  if (!config.optional && !hasDefault && (config.enum ?? []).every(option => option)) {
    // make sure that falsy enum values e.g. "" are not flagged by required validator
    validators.push(Validators.required);
  }
  if (config.minLength > 0) {
    validators.push(Validators.minLength(config.minLength));
  }
  if (config.maxLength > 0) {
    validators.push(Validators.maxLength(config.maxLength));
  }
  if (isString(config.pattern)) {
    validators.push(Validators.pattern(config.pattern));
  }
  if (config.type === 'integer') {
    validators.push(typeNumber, numberInteger);
  }
  if (config.type === 'number') {
    validators.push(typeNumber);
  }
  if (isNumber(config.minimum)) {
    validators.push(Validators.min(config.minimum));
  }
  if (isNumber(config.maximum)) {
    validators.push(Validators.max(config.maximum));
  }
  return validators;
}

export function getConfigArrayValidators(config: GearConfigArray): ValidatorFn[] {
  const validators = [];
  const hasDefault = ('default' in config);
  if (!config.optional && !hasDefault) {
    validators.push(Validators.required);
  }
  if (config.minItems > 0) {
    validators.push(Validators.minLength(config.minItems));
  }
  if (config.maxItems > 0) {
    validators.push(Validators.maxLength(config.maxItems));
  }
  if (config.items) {
    const itemValidators = getConfigValidators(config.items);
    if (itemValidators.length > 0) {
      const arrayValidator = arrayItemValidator(Validators.compose(itemValidators));
      validators.push(arrayValidator);
    }
  }
  return validators;
}

export function isConfigArray(config: GearConfigParameter): config is GearConfigArray {
  return config.type === 'array';
}

/**
 * Convert string form field values to match schema of gear config.
 */
export function coerceConfig(
  values: Record<string, ParameterValue | ParameterValue[]>,
  config: Record<string, GearConfigParameter>,
): Record<string, ParameterValue | ParameterValue[]> {
  const result = {};
  for (const [key, value] of Object.entries(values)) {
    const schema = config[key];
    if (schema) {
      if (isConfigArray(schema)) {
        result[key] = (value as ParameterValue[]).map(v => coerceValue(v, schema.items));
      } else {
        result[key] = coerceValue(value as ParameterValue, schema);
      }
    }
  }
  return result;
}

function coerceValue(value: ParameterValue, config: GearConfigParameter): ParameterValue {
  if (isNil(value)) {
    return config.default as ParameterValue; // could be undefined and that is OK
  }
  switch (config.type) {
    case 'boolean':
      return Boolean(value);
    case 'integer':
    case 'number':
      return Number(value);
    default:
      return value;
  }
}
